// EventCard.tsx
import { Avatar, AvatarGroup, Box, Button, Chip, Typography } from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useGoalngoEventFormatter } from "../../../hooks/useGoalngoEventFormatter";
import { EventDetailsDateIcon, EventDetailsLocationIcon } from "../../../assets/icons/MUI/EventsIcons";
import { CircularProgressWithLabel } from "../../UI/CircularProgressWithLabel";
import { getEventRoute } from "../../../routers/routeUtils";
import { useCancelEvent } from "../../../api/queries/events/eventsListMutations";
import { useProfileData } from "../../../hooks/useProfileData";

import ConfirmCancelEventPopUp from "./ConfirmCancelEventPopUp";
import { GoalngoEvent } from "../../../types/types";
import { styleConfig } from "./EventCard.styles";
import EventCardHeader from "./EventCardHeader";
import EventCardOrganizer from "./EventCardOrganizer";

const EventCard = ({ event }: { event: GoalngoEvent }) => {
  const {
    data: { id: userId },
  } = useProfileData();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [cancelEventState, setCancelEventState] = useState({
    isCanceled: false,
    isPopUpOpen: false,
    reason: null as { id: number; title: string } | null,
    comment: null as string | null,
  });

  const {
    id,
    name,
    totalPlayersInvitedNum,
    acceptedPlayersNum,
    startingDay,
    startingMonth,
    startingHour,
    endHour,
    eventPrivacy,
    eventType,
    status,
    eventStatus,
    eventLocation,
    invitedPlayersAvatars,
    authorId,
    authorName,
    authorMedia,
    authorFirstTwoChr,
  } = useGoalngoEventFormatter(event);

  const { mutate: cancelEventMutation } = useCancelEvent(
    id,
    () => {
      setCancelEventState((prev) => ({ ...prev, isCanceled: true, isPopUpOpen: false }));
    },
    () => {
      setCancelEventState((prev) => ({ ...prev, isPopUpOpen: false }));
    }
  );

  const handleCancelEvent = () => {
    const body = {
      cancelEventReason: cancelEventState.reason?.title,
      cancelEventComment: cancelEventState.comment,
    };
    cancelEventMutation(body);
  };

  const isEventFinishedOrCancelled =
    event.status === "FINISHED" || event.status === "CANCELLED" || cancelEventState.isCanceled;
  const canCancelEvent = userId === authorId && !isEventFinishedOrCancelled;

  return (
    <Box sx={styleConfig.cardContainer}>
      <EventCardHeader
        eventType={eventType}
        eventStatus={eventStatus}
        status={status}
        isCanceled={cancelEventState.isCanceled}
      />

      <Box sx={styleConfig.contentSection}>
        <Typography variant="body16ExtraBold" sx={styleConfig.title}>
          {name}
        </Typography>

        <Box sx={styleConfig.detailsContainer}>
          <Box sx={styleConfig.dateContainer}>
            <Typography variant="h4ExtraBold" sx={styleConfig.dateText}>
              {startingDay}
            </Typography>
            <Typography variant="h4ExtraBold" sx={styleConfig.dateText}>
              {startingMonth}
            </Typography>
          </Box>

          <Box sx={styleConfig.infoContainer}>
            <Box sx={styleConfig.locationRow}>
              <EventDetailsLocationIcon />
              <Typography variant="body12Regular" sx={styleConfig.locationText}>
                {eventLocation}
              </Typography>
            </Box>

            <Typography variant="body12Regular" sx={styleConfig.timeRow}>
              <EventDetailsDateIcon />
              {startingHour} - {endHour} . {eventPrivacy}
            </Typography>
          </Box>

          <CircularProgressWithLabel value={acceptedPlayersNum} totalNum={totalPlayersInvitedNum} />
        </Box>
      </Box>

      <Box sx={styleConfig.footerSection}>
        <EventCardOrganizer authorName={authorName} authorMedia={authorMedia} authorFirstTwoChr={authorFirstTwoChr} />

        <AvatarGroup sx={styleConfig.avatarGroup} total={acceptedPlayersNum}>
          {invitedPlayersAvatars.map((player, idx) => (
            <Avatar key={idx} alt={player.name} src={player.media}>
              {player.firstTwoChr}
            </Avatar>
          ))}
        </AvatarGroup>
      </Box>

      <Box sx={styleConfig.actionButtons}>
        {canCancelEvent ? (
          <>
            <Button variant="primary-sm" sx={styleConfig.manageButton} onClick={() => navigate(getEventRoute(id))}>
              {t("pages.events.eventList.managePlayers")}
            </Button>
            <Button
              variant="outlined-sm"
              sx={styleConfig.cancelButton}
              onClick={() => setCancelEventState((prev) => ({ ...prev, isPopUpOpen: true }))}
            >
              {t("pages.events.eventList.cancelMatch")}
            </Button>
          </>
        ) : (
          <Button variant="primary-sm" fullWidth onClick={() => navigate(getEventRoute(id))}>
            {t("pages.events.eventList.viewDetails")}
          </Button>
        )}
      </Box>

      <ConfirmCancelEventPopUp
        openDialog={cancelEventState.isPopUpOpen}
        handleCloseDialog={() => setCancelEventState((prev) => ({ ...prev, isPopUpOpen: false }))}
        handleCancelEvent={handleCancelEvent}
        cancelEventState={cancelEventState}
        setCancelEventState={setCancelEventState}
      />
    </Box>
  );
};

export default EventCard;
